/**
 * Everything the robot lanes need for one window, gathered and shaped.
 *
 * The screen already holds the live tasks, the schedule occurrences, the
 * schedules and the maintenance windows (it reads them for its banners as
 * well); this adds the robots, the robot groups, the finished tasks the
 * live list no longer holds, and the fire events, then hands all of it to
 * the pure builders. Components draw `LaneData` and fetch nothing, so the
 * phone views to come can reuse this hook as it is.
 */
import { useMemo } from 'react';
import { useRobots } from '../../../api/hooks';
import {
  useFireEventsInWindow,
  useTaskCompletionsInWindow,
} from '../../../api/hooks/useCalendarHistory';
import type { TaskState } from '../../../api/rmf-client';
import type {
  FireEvent,
  LiftRuleOccurrence,
  MaintenanceWindow,
  ScheduleOccurrence,
  TaskCompletionRecord,
  TaskSchedule,
} from '../../../api/v1';
import { LINEN_LABEL, paintFor } from '../calendar-categories';
import { buildLaneItems, titleOf, type LaneItem } from './lane-items';
import type { GroupDef, RobotEntry } from './lane-layout';
import type { TimeWindow } from './lane-time';
import {
  linenRobotKeys,
  resolveRobotGroups,
  useRobotGroups,
  type ResolvedGroups,
} from './robot-groups';
import { buildSiteBars, type SiteBar } from './site-row';
import {
  liftRulesOn,
  useClosedLiftScopes,
  useLiftRuleOccurrences,
  useLiftRules,
} from '../../../api/hooks/useLiftRules';
import type { ClosedLiftScope } from '../../../state/lift-rule-waiting';
import type { RobotStateView } from '../../../api/rmf-client';

export interface LaneData {
  items: LaneItem[];
  robots: RobotEntry[];
  groups: GroupDef[];
  groupsMode: ResolvedGroups['mode'];
  groupsSource: ResolvedGroups['source'];
  canEditGroups: boolean;
  siteBars: SiteBar[];
  /** Only the open fire event is known (an api-server without history). */
  fireHistoryMissing: boolean;
  /** The finished-task read for this window is still in flight. */
  historyLoading: boolean;
  /** It failed: a past day may be missing tasks. */
  historyError: boolean;
  /** It stopped at its page cap: a past week may be missing tasks. */
  historyPartial: boolean;
}

export interface LaneDataInputs {
  window: TimeWindow;
  nowMs: number;
  tasks: readonly TaskState[];
  completions?: readonly TaskCompletionRecord[];
  occurrences?: readonly ScheduleOccurrence[];
  schedules?: readonly TaskSchedule[];
  maintenance?: readonly MaintenanceWindow[];
  /** Runs of lift rules, for the Site row. */
  liftRules?: readonly LiftRuleOccurrence[];
  /** The lift scopes closed now, for what a waiting task says. */
  liftClosed?: readonly ClosedLiftScope[] | null;
  fires?: readonly FireEvent[];
  groups: ResolvedGroups;
}

/** Pure: the shaped data for one window. */
export function buildLaneData(
  inp: LaneDataInputs,
): Pick<
  LaneData,
  'items' | 'robots' | 'groups' | 'groupsMode' | 'groupsSource' | 'canEditGroups' | 'siteBars'
> {
  const items = paintLinenRuns(
    buildLaneItems({
      live: inp.tasks,
      completions: inp.completions,
      occurrences: inp.occurrences,
      schedules: inp.schedules,
      robotKeys: inp.groups.robots.map((r) => r.key),
      window: inp.window,
      nowMs: inp.nowMs,
      liftClosed: inp.liftClosed,
    }),
    linenRobotKeys(inp.groups.robots, inp.groups.groups),
  );
  return {
    items,
    robots: inp.groups.robots,
    groups: inp.groups.groups,
    groupsMode: inp.groups.mode,
    groupsSource: inp.groups.source,
    canEditGroups: inp.groups.canEdit,
    siteBars: buildSiteBars(
      inp.maintenance ?? [],
      inp.fires ?? [],
      inp.window,
      inp.nowMs,
      inp.liftRules ?? [],
    ),
  };
}

/** Deliveries run by a linen robot, drawn (and named) as linen runs. */
export function paintLinenRuns(items: LaneItem[], linen: ReadonlySet<string>): LaneItem[] {
  if (linen.size === 0) return items;
  return items.map((it) =>
    paintFor(it.lane, it.robotKey, linen) === 'linen'
      ? {
          ...it,
          paint: 'linen',
          typeLabel: LINEN_LABEL,
          title: it.kind === 'task' ? titleOf(LINEN_LABEL, it.place) : it.title,
        }
      : it,
  );
}

/**
 * Whether the window reaches back before the live list's coverage, so
 * the durable record has to be read for it. The live list holds every
 * task inside the site's live retention; its oldest record marks where
 * that stops. A window entirely in the future never needs it.
 */
export function needsCompletions(
  window: TimeWindow,
  tasks: readonly TaskState[],
  nowMs: number,
): boolean {
  if (window.fromMs >= nowMs) return false;
  let oldest = Infinity;
  for (const t of tasks) {
    const at = t.submittedAtMs ?? t.completedAtMs;
    if (typeof at === 'number' && Number.isFinite(at) && at < oldest) oldest = at;
  }
  return window.fromMs < oldest;
}

const NO_ROBOTS: RobotStateView[] = [];
/** The api-server's own limit on one occurrences read. */
const LIFT_RULE_RANGE_MS = 62 * 86_400_000;

export function useCalendarLaneData(args: {
  siteId: string;
  window: TimeWindow | null;
  nowMs: number;
  tasks: readonly TaskState[];
  occurrences: readonly ScheduleOccurrence[];
  schedules: readonly TaskSchedule[];
  maintenance: readonly MaintenanceWindow[];
}): LaneData {
  const { siteId, window, nowMs, tasks, occurrences, schedules, maintenance } = args;
  const robots = useRobots();
  const serverGroups = useRobotGroups(siteId);
  const needed = window !== null && needsCompletions(window, tasks, nowMs);
  const completions = useTaskCompletionsInWindow(siteId, window, needed);
  const fires = useFireEventsInWindow(siteId, window);
  // Read here, not by each view: every view's Site row draws the same bands.
  const rulesOn = liftRulesOn(useLiftRules().state);
  const liftRange = useMemo(
    () =>
      rulesOn && window && window.toMs - window.fromMs <= LIFT_RULE_RANGE_MS
        ? { fromMs: window.fromMs, toMs: window.toMs }
        : null,
    [rulesOn, window],
  );
  const liftOccurrences = useLiftRuleOccurrences(liftRange).data?.occurrences;
  // Polled, so a new array each time: its content is what matters.
  const closedNow = useClosedLiftScopes();
  const closedKey = JSON.stringify(closedNow);
  const liftClosed = useMemo(
    () => JSON.parse(closedKey) as typeof closedNow,
    [closedKey],
  );

  const liveRobots = robots.data ?? NO_ROBOTS;
  // `undefined` while loading or failed -> derive meanwhile.
  const server = serverGroups.isSuccess ? serverGroups.data : undefined;
  const groups = useMemo(
    () => resolveRobotGroups({ server, liveRobots, tasks }),
    [server, liveRobots, tasks],
  );

  const completionEntries = needed ? completions.data?.entries : undefined;
  const fireEvents = fires.data?.events;
  const shaped = useMemo(
    () =>
      window
        ? buildLaneData({
            window,
            nowMs,
            tasks,
            completions: completionEntries,
            occurrences,
            schedules,
            maintenance,
            fires: fireEvents,
            groups,
            liftRules: liftOccurrences,
            liftClosed,
          })
        : null,
    [
      window,
      nowMs,
      tasks,
      completionEntries,
      occurrences,
      schedules,
      maintenance,
      fireEvents,
      groups,
      liftOccurrences,
      liftClosed,
    ],
  );

  return {
    items: shaped?.items ?? [],
    robots: shaped?.robots ?? groups.robots,
    groups: shaped?.groups ?? groups.groups,
    groupsMode: groups.mode,
    groupsSource: groups.source,
    canEditGroups: groups.canEdit,
    siteBars: shaped?.siteBars ?? [],
    fireHistoryMissing: fires.data?.source === 'current',
    historyLoading: needed && completions.isLoading,
    historyError: needed && completions.isError,
    historyPartial: needed && completions.data?.complete === false,
  };
}
